import { useParams, Link } from 'react-router-dom'
import { useQueries } from '@tanstack/react-query'
import { useAppState } from './AppLayout'
import { StopInfoMap } from './StopInfoMap'
import { useStopDetails, useRoutesForStop, useStopArrivals } from '@/lib/queries'
import { greekToLatin, api } from '@/lib/api'
import type { StopInfo, RouteForStop } from '@/lib/api'
import { DataError } from './DataError'

export function StopInfoPage() {
  const { stopCode } = useParams<{ stopCode: string }>()
  const { lang, t } = useAppState()

  const stopQuery = useStopDetails(stopCode)
  const routesQuery = useRoutesForStop(stopCode)
  const arrivalsQuery = useStopArrivals(stopCode)
  const { data: stopDetails, isLoading: stopLoading } = stopQuery
  const { data: routesForStop, isLoading: routesLoading } = routesQuery
  const { data: arrivals } = arrivalsQuery

  const routeStopsQueries = useQueries({
    queries: (routesForStop || []).map((route: RouteForStop) => ({
      queryKey: ['stops', route.RouteCode],
      queryFn: () => api.getStops(route.RouteCode),
      staleTime: 5 * 60 * 1000,
      enabled: !!route.RouteCode,
    })),
  })

  const isLoading = stopLoading || routesLoading
  const isLoadingRouteStops = routeStopsQueries.some(q => q.isLoading)
  const dataError = stopQuery.error || routesQuery.error || arrivalsQuery.error || routeStopsQueries.find(q => q.error)?.error

  const routesWithStops = (routesForStop || []).map((route: RouteForStop, index: number) => ({
    route,
    stops: (routeStopsQueries[index]?.data || []) as StopInfo[],
  }))

  const arrivalsByRoute = new Map<string, number>()
  if (arrivals) {
    arrivals.forEach(arr => {
      const existing = arrivalsByRoute.get(arr.route_code)
      const minutes = parseInt(arr.btime2)
      if (!existing || minutes < existing) {
        arrivalsByRoute.set(arr.route_code, minutes)
      }
    })
  }

  if (isLoading) {
    return <div className="loading">{t.loading}</div>
  }

  if (dataError) {
    return (
      <DataError
        error={dataError}
        lang={lang}
        onRetry={() => {
          void stopQuery.refetch()
          void routesQuery.refetch()
          void arrivalsQuery.refetch()
          routeStopsQueries.forEach(query => void query.refetch())
        }}
      />
    )
  }

  if (!stopDetails) {
    return (
      <div className="error-message">
        {lang === 'en' ? 'Stop not found' : 'Η στάση δεν βρέθηκε'}
      </div>
    )
  }

  const stopName = lang === 'en'
    ? (stopDetails.stop_descr_eng || stopDetails.stop_descr)
    : stopDetails.stop_descr

  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${stopDetails.stop_lat},${stopDetails.stop_lng}`

  return (
    <div className="stop-info-page">
        <div className="stop-info-header">
          <h1 className="stop-name">{stopName}</h1>
          <div className="stop-code">#{stopCode}</div>
          <a
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="directions-link"
          >
            {t.getDirections}
          </a>
        </div>

        {!isLoadingRouteStops && routesWithStops.length > 0 && (
          <StopInfoMap
            stop={stopDetails}
            routesWithStops={routesWithStops}
            lang={lang}
          />
        )}

        <div className="stop-routes-section">
          <h2>{lang === 'en' ? 'Lines serving this stop' : 'Γραμμές που εξυπηρετούν αυτή τη στάση'}</h2>
          {routesForStop && routesForStop.length > 0 ? (
            <div className="stop-routes-list">
              {routesForStop.map((route: RouteForStop) => {
                const arrivalMinutes = arrivalsByRoute.get(route.RouteCode)
                const lineSlug = greekToLatin(route.LineID).toLowerCase()
                const routeDesc = lang === 'en'
                  ? (route.RouteDescrEng || route.RouteDescr)
                  : route.RouteDescr

                return (
                  <Link
                    key={route.RouteCode}
                    to={`/${lineSlug}/${route.RouteCode}/${stopCode}`}
                    className="stop-route-item"
                  >
                    <div className="route-line-badge">{route.LineID}</div>
                    <div className="route-details">
                      <div className="route-description">{routeDesc}</div>
                      {arrivalMinutes !== undefined && (
                        <div className="route-arrival">
                          <span className="arrival-time">{arrivalMinutes}</span>
                          <span className="arrival-unit">{t.min}</span>
                        </div>
                      )}
                    </div>
                  </Link>
                )
              })}
            </div>
          ) : (
            <div className="no-routes">
              {lang === 'en' ? 'No routes found for this stop' : 'Δεν βρέθηκαν γραμμές για αυτή τη στάση'}
            </div>
          )}
        </div>
    </div>
  )
}
